<template>
  <router-view />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { Monitor, User, Key, Document, Fold, CaretBottom } from '@element-plus/icons-vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import { getToken } from '@/utils/auth'

const store = useStore()
const router = useRouter()

const userInfo = computed(() => store.state.user.userInfo)

const handleProfile = () => {
  router.push('/profile')
}

const handleLogout = async () => {
  await store.dispatch('user/logout')
  router.push('/login')
}

const toggleSidebar = () => {
  store.commit('app/TOGGLE_SIDEBAR')
}

// 添加全局错误处理，用于捕获ResizeObserver错误
let errorHandler = null

// 处理ResizeObserver警告错误
let errorEventListener = null
let rejectionEventListener = null

// 初始化全局ResizeObserver错误处理
const setupResizeObserverErrorHandling = () => {
  // 处理常规错误
  errorEventListener = (event) => {
    if (event && event.message && (
        event.message.includes('ResizeObserver') || 
        event.message.includes('ResizeObserver loop') || 
        event.message.includes('undelivered notifications'))) {
      event.stopImmediatePropagation()
      event.preventDefault()
      event.stopPropagation()
    }
  }
  
  // 处理Promise错误
  rejectionEventListener = (event) => {
    if (event.reason && event.reason.message && (
        event.reason.message.includes('ResizeObserver') || 
        event.reason.message.includes('ResizeObserver loop') || 
        event.reason.message.includes('undelivered notifications'))) {
      event.stopImmediatePropagation()
      event.preventDefault()
      event.stopPropagation()
    }
  }

  // 使用捕获模式添加事件监听器
  window.addEventListener('error', errorEventListener, { capture: true })
  window.addEventListener('unhandledrejection', rejectionEventListener, { capture: true })
  
  // 添加控制台拦截 - 阻止显示错误
  const originalConsoleError = console.error
  console.error = function(...args) {
    // 过滤掉ResizeObserver相关的错误
    if (args.length > 0 && 
        typeof args[0] === 'string' && 
        (args[0].includes('ResizeObserver') || 
         args[0].includes('ResizeObserver loop'))) {
      // 忽略ResizeObserver错误
      return
    }
    
    // 其他错误正常输出
    return originalConsoleError.apply(this, args)
  }
}

onMounted(() => {
  // 添加全局错误处理函数
  errorHandler = (event) => {
    // 过滤掉ResizeObserver错误，避免在控制台显示
    if (event && event.message && event.message.includes('ResizeObserver')) {
      event.stopPropagation()
      event.preventDefault()
    }
  }
  
  // 注册错误处理函数
  window.addEventListener('error', errorHandler, true)
  
  // 检查token是否存在，如果不存在则自动退出
  if (!getToken() && router.currentRoute.value.path !== '/login') {
    store.dispatch('user/resetToken').then(() => {
      router.push('/login')
    })
  }

  setupResizeObserverErrorHandling()
})

onUnmounted(() => {
  // 移除错误处理函数，避免内存泄漏
  if (errorHandler) {
    window.removeEventListener('error', errorHandler, true)
  }
})

onBeforeUnmount(() => {
  if (errorEventListener) {
    window.removeEventListener('error', errorEventListener, { capture: true })
  }
  if (rejectionEventListener) {
    window.removeEventListener('unhandledrejection', rejectionEventListener, { capture: true })
  }
})
</script>

<style lang="scss">
.app-wrapper {
  height: 100vh;
  
  .sidebar-container {
    background-color: #304156;
    height: 100%;
    
    .el-menu {
      border-right: none;
    }
  }

  .app-header {
    background-color: #fff;
    border-bottom: 1px solid #dcdfe6;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 15px;

    .header-left {
      display: flex;
      align-items: center;

      .toggle-sidebar {
        padding: 0 15px;
        cursor: pointer;
        font-size: 20px;
        color: #606266;
        
        &:hover {
          color: #409EFF;
        }
      }
    }

    .header-right {
      .user-dropdown {
        cursor: pointer;
        color: #606266;
        display: flex;
        align-items: center;
        
        .el-icon {
          margin-left: 5px;
        }
      }
    }
  }

  .el-main {
    background-color: #f0f2f5;
    padding: 20px;
  }
}

// 路由切换动画
.fade-transform-enter-active,
.fade-transform-leave-active {
  transition: all .3s;
}

.fade-transform-enter-from {
  opacity: 0;
  transform: translateX(-30px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
